(() => {
  const BUILDER_VERSION = '20.4.25';
  const SETUP_KEY = 'ops-event-guide-setup-v2';

  const init = (root) => {
    if (!root || root.dataset.opsEventReady === 'true') return;
    root.dataset.opsEventReady = 'true';

    const currency = root.dataset.currency || 'GBP';
    const workerUrl = String(root.dataset.workerUrl || '').replace(/\/$/, '');
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency', currency, minimumFractionDigits: 2
    });

    const el = {
      tray: root.querySelector('[data-ops-tray]'),
      trayItems: root.querySelector('[data-ops-tray-items]'),
      trayCount: root.querySelector('[data-ops-tray-count]'),
      trayTotal: root.querySelector('[data-ops-tray-total]'),
      clear: root.querySelector('[data-ops-clear-setup]'),
      review: root.querySelector('[data-ops-review-setup]'),
      mobileTray: root.querySelector('[data-ops-mobile-tray]'),
      mobileCount: root.querySelector('[data-ops-mobile-count]'),
      mobileTotal: root.querySelector('[data-ops-mobile-total]'),
      reviewDialog: root.querySelector('[data-ops-review-dialog]'),
      reviewClose: root.querySelector('[data-ops-review-close]'),
      reviewList: root.querySelector('[data-ops-review-list]'),
      reviewStatus: root.querySelector('[data-ops-review-status]'),
      addCart: root.querySelector('[data-ops-add-cart]')
    };

    const state = { selected: new Map(), busy: false, artwork: new Map() };

    const makeSessionId = (item) => `event-${Date.now().toString(36)}-${String(item.variantId)}-${Math.random().toString(36).slice(2, 9)}`;
    const safeFileName = (name = 'artwork') => String(name).normalize('NFKD').replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_').replace(/^[_\.]+|[_\.]+$/g, '').slice(0, 160) || 'artwork';

    const uploadArtwork = async (item, file, statusNode) => {
      if (!workerUrl) throw new Error('Artwork upload is not configured for this page.');
      if (!(file instanceof File)) throw new Error('Choose an artwork file first.');
      const sessionId = makeSessionId(item);
      const materialised = new File([await file.arrayBuffer()], safeFileName(file.name), { type: file.type || 'application/octet-stream', lastModified: Date.now() });
      const form = new FormData();
      form.append('file', materialised, materialised.name);
      form.append('sessionId', sessionId);
      form.append('fileRole', 'original');
      form.append('productTitle', item.title);
      form.append('productHandle', item.handle);
      form.append('variantTitle', item.variantTitle);
      form.append('previewType', 'event-guide-review');
      form.append('selectedOptions', JSON.stringify([{ name: 'Variant', value: item.variantTitle }, { name: 'Quantity', value: String(item.quantity) }]));
      if (statusNode) statusNode.textContent = `Uploading ${materialised.name}…`;
      const response = await fetch(`${workerUrl}/upload`, { method: 'POST', body: form });
      const text = await response.text();
      let result = {};
      try { result = text ? JSON.parse(text) : {}; } catch (_) { result = { error: text }; }
      if (!response.ok || result?.ok !== true || result?.error) throw new Error(result?.error || result?.message || 'Artwork could not be uploaded.');
      const verifyPreviewUrl = async (url) => {
        if (!url) return '';
        try {
          const check = await fetch(url, { method: 'HEAD', credentials: 'omit', cache: 'no-store' });
          const type = check.headers.get('content-type') || '';
          return check.ok && /^image\/(png|jpeg|webp|svg\+xml)/i.test(type) ? url : '';
        } catch (_) {
          return '';
        }
      };

      const uploadPreview = async () => {
        if (!/^image\/(png|jpeg|webp)$/i.test(materialised.type || '')) return null;
        let bitmap;
        try {
          bitmap = await createImageBitmap(materialised);
          const maxEdge = 1600;
          const ratio = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
          const width = Math.max(1, Math.round(bitmap.width * ratio));
          const height = Math.max(1, Math.round(bitmap.height * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return null;
          ctx.drawImage(bitmap, 0, 0, width, height);
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
          if (!blob) return null;
          const previewFile = new File([blob], `${materialised.name.replace(/\.[^.]+$/, '') || 'artwork'}-preview.png`, { type: 'image/png', lastModified: Date.now() });
          const previewForm = new FormData();
          previewForm.append('file', previewFile, previewFile.name);
          previewForm.append('sessionId', result.sessionId || sessionId);
          previewForm.append('fileRole', 'customer-preview');
          previewForm.append('productTitle', item.title);
          previewForm.append('productHandle', item.handle);
          previewForm.append('variantTitle', item.variantTitle);
          previewForm.append('previewType', 'event-guide-review');
          previewForm.append('selectedOptions', JSON.stringify([{ name: 'Variant', value: item.variantTitle }, { name: 'Quantity', value: String(item.quantity) }]));
          const previewResponse = await fetch(`${workerUrl}/upload`, { method: 'POST', body: previewForm });
          const previewText = await previewResponse.text();
          let previewResult = {};
          try { previewResult = previewText ? JSON.parse(previewText) : {}; } catch (_) { previewResult = { error: previewText }; }
          if (!previewResponse.ok || previewResult?.ok !== true || previewResult?.error) return null;
          return previewResult;
        } catch (_) {
          return null;
        } finally {
          try { bitmap?.close?.(); } catch (_) {}
        }
      };

      const previewResult = await uploadPreview();
      const rawPreviewUrl = previewResult?.previewUrl || '';
      const verifiedPreviewUrl = await verifyPreviewUrl(rawPreviewUrl);
      const finalResult = previewResult || result;
      const record = {
        name: materialised.name,
        sessionId: finalResult.sessionId || result.sessionId || sessionId,
        uploadReference: finalResult.uploadReference || result.uploadReference || '',
        fileUrl: result.fileUrl || '',
        previewUrl: verifiedPreviewUrl,
        zipUrl: finalResult.zipUrl || result.zipUrl || ''
      };
      state.artwork.set(item.id, record);
      if (statusNode) statusNode.textContent = verifiedPreviewUrl ? `${record.name} uploaded` : `${record.name} uploaded · preview unavailable`;
      return record;
    };

    const setReviewStatus = (message, error = false) => {
      if (!el.reviewStatus) return;
      el.reviewStatus.textContent = message || '';
      el.reviewStatus.classList.toggle('is-error', error);
    };

    const selectionFromCard = (card) => {
      const variant = card.querySelector('[data-ops-variant-select]');
      const qty = card.querySelector('[data-ops-quantity]');
      const option = variant?.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
      const variantId = Number(variant?.value || 0);
      if (!variantId) return null;
      return {
        id: `${card.dataset.productHandle}:${variantId}`,
        handle: card.dataset.productHandle || '',
        title: card.dataset.productTitle || '',
        variantId,
        variantTitle: option?.dataset?.title || option?.textContent?.split(' · ')[0] || 'Default',
        price: Number(option?.dataset?.price || 0),
        quantity: Math.max(1, Number(qty?.value || 1))
      };
    };

    const saveSetup = () => {
      try { localStorage.setItem(SETUP_KEY, JSON.stringify([...state.selected.values()])); } catch (_) {}
    };

    const renderTray = () => {
      const items = [...state.selected.values()];
      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      el.trayItems.innerHTML = '';
      if (!items.length) {
        const p = document.createElement('p');
        p.textContent = 'Nothing added yet.';
        el.trayItems.appendChild(p);
      }
      items.forEach((item) => {
        const row = document.createElement('div'); row.className = 'ops-event-guide__tray-item';
        const copy = document.createElement('div');
        const strong = document.createElement('strong'); strong.textContent = item.title;
        const small = document.createElement('span'); small.textContent = `${item.variantTitle}${item.quantity > 1 ? ` × ${item.quantity}` : ''}`;
        copy.append(strong, small);
        const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = '×';
        remove.setAttribute('aria-label', `Remove ${item.title}`);
        remove.addEventListener('click', () => { state.selected.delete(item.id); saveSetup(); renderTray(); });
        row.append(copy, remove); el.trayItems.appendChild(row);
      });
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      el.trayCount.textContent = `${count} item${count === 1 ? '' : 's'}`;
      el.trayTotal.textContent = money.format(total / 100);
      el.review.disabled = !items.length;
      el.tray.classList.toggle('is-visible', items.length > 0);
      if (items.length) {
        el.mobileTray.hidden = false;
        el.mobileCount.textContent = `${count} item${count === 1 ? '' : 's'}`;
        el.mobileTotal.textContent = money.format(total / 100);
      } else {
        el.mobileTray.hidden = true;
      }
    };

    const addFromCard = (card) => {
      const item = selectionFromCard(card);
      if (!item) return;
      const existing = state.selected.get(item.id);
      if (existing) item.quantity += existing.quantity;
      state.selected.set(item.id, item);
      saveSetup(); renderTray();
      const button = card.querySelector('[data-ops-add-product]');
      if (button) {
        const old = button.textContent;
        button.textContent = 'Added';
        setTimeout(() => { button.textContent = old; }, 900);
      }
    };

    const findCard = (handle) => [...root.querySelectorAll('[data-ops-product-card]')]
      .find((card) => card.dataset.productHandle === handle);

    const addPreset = (name) => {
      const presets = {
        market: ['essential-roller-banner-stand', 'business-cards-1', 'leaflets'],
        exhibition: ['custom-fast-fold-led-fabric-lightbox', 'custom-fast-fold-led-display-counter', 'leaflets'],
        outdoor: ['custom-full-fibre-flags', 'full-fibre-flag-bases', 'flag-water-ballast-weights']
      };
      (presets[name] || []).forEach((handle) => {
        const card = findCard(handle);
        if (card) addFromCard(card);
      });
    };

    const restoreSetup = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(SETUP_KEY) || '[]');
        if (Array.isArray(stored)) stored.forEach((item) => {
          if (item?.id && item?.variantId) state.selected.set(item.id, item);
        });
      } catch (_) {}
      renderTray();
    };

    const renderReview = () => {
      const items = [...state.selected.values()];
      el.reviewList.innerHTML = '';
      items.forEach((item) => {
        const row = document.createElement('div'); row.className = 'ops-event-guide__review-row';
        const top = document.createElement('div'); top.className = 'ops-event-guide__review-product';
        const copy = document.createElement('div');
        const strong = document.createElement('strong'); strong.textContent = item.title;
        const small = document.createElement('span'); small.textContent = `${item.variantTitle} · Qty ${item.quantity}`;
        copy.append(strong, small);
        const price = document.createElement('strong'); price.textContent = money.format((item.price * item.quantity) / 100);
        top.append(copy, price);

        const artwork = document.createElement('div'); artwork.className = 'ops-event-guide__review-artwork';
        const label = document.createElement('div');
        const labelStrong = document.createElement('strong'); labelStrong.textContent = 'Artwork for this product';
        const labelSmall = document.createElement('span'); labelSmall.textContent = state.artwork.has(item.id) ? state.artwork.get(item.id).name : 'Upload now, or send it after ordering with your order number.';
        label.append(labelStrong, labelSmall);
        const input = document.createElement('input'); input.type = 'file'; input.hidden = true; input.accept = '.png,.jpg,.jpeg,.webp,.svg,.pdf,image/png,image/jpeg,image/webp,image/svg+xml,application/pdf';
        const button = document.createElement('button'); button.type = 'button'; button.className = 'button button--secondary'; button.textContent = state.artwork.has(item.id) ? 'Replace artwork' : 'Upload artwork';
        const status = document.createElement('span'); status.className = 'ops-event-guide__artwork-status';
        if (state.artwork.has(item.id)) status.textContent = 'Uploaded';
        button.addEventListener('click', () => input.click());
        input.addEventListener('change', async () => {
          const file = input.files?.[0]; if (!file) return;
          button.disabled = true;
          try { await uploadArtwork(item, file, status); button.textContent = 'Replace artwork'; labelSmall.textContent = state.artwork.get(item.id)?.name || file.name; }
          catch (error) { status.textContent = error?.message || 'Upload failed'; status.classList.add('is-error'); }
          finally { button.disabled = false; }
        });
        artwork.append(label, button, input, status);
        row.append(top, artwork); el.reviewList.appendChild(row);
      });
      setReviewStatus('');
      if (typeof el.reviewDialog.showModal === 'function') el.reviewDialog.showModal();
      else el.reviewDialog.setAttribute('open', '');
    };

    const addToCart = async () => {
      if (state.busy || !state.selected.size) return;
      state.busy = true;
      el.addCart.disabled = true;
      el.addCart.textContent = 'Adding setup…';
      const setupRef = `EVENT-${Date.now().toString(36).toUpperCase()}`;
      try {
        const items = [...state.selected.values()].map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
          properties: (() => {
            const art = state.artwork.get(item.id);
            const properties = {
              '_Event setup': setupRef,
              '_Event guide': 'Event Print, Displays and Everything That Goes With Them',
              '_Artwork status': art ? 'Uploaded through event guide final check' : 'Not supplied through event guide',
              '_builder_version': BUILDER_VERSION
            };
            if (art?.zipUrl) properties['_Artwork files'] = art.zipUrl;
            else if (art?.fileUrl) properties['_Artwork file'] = art.fileUrl;
            if (art?.previewUrl) properties['_Artwork preview'] = art.previewUrl;
            if (art?.uploadReference) properties['_upload_reference'] = art.uploadReference;
            if (art?.sessionId) properties['_upload_session_id'] = art.sessionId;
            if (art?.name) properties['Artwork file'] = art.name;
            return properties;
          })()
        }));
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        const text = await response.text();
        let result = {};
        try { result = text ? JSON.parse(text) : {}; } catch (_) { result = { description: text }; }
        if (!response.ok || result?.status) throw new Error(result?.description || result?.message || 'The setup could not be added to basket.');
        localStorage.removeItem(SETUP_KEY);
        state.selected.clear(); renderTray();
        setReviewStatus('Setup added. Opening your basket…');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (error) {
        setReviewStatus(error?.message || 'Something went wrong while adding the setup.', true);
      } finally {
        state.busy = false;
        el.addCart.disabled = false;
        el.addCart.textContent = 'Add complete setup to basket';
      }
    };

    root.addEventListener('click', (event) => {
      const add = event.target.closest('[data-ops-add-product]');
      if (add) {
        const card = add.closest('[data-ops-product-card]');
        if (card) addFromCard(card);
        return;
      }
      const preset = event.target.closest('[data-ops-preset]');
      if (preset) addPreset(preset.dataset.opsPreset);
    });

    el.clear?.addEventListener('click', () => { state.selected.clear(); saveSetup(); renderTray(); });
    el.review?.addEventListener('click', renderReview);
    el.mobileTray?.addEventListener('click', renderReview);
    el.reviewClose?.addEventListener('click', () => el.reviewDialog.close());
    el.addCart?.addEventListener('click', addToCart);

    restoreSetup();
  };

  const boot = () => document.querySelectorAll('[data-ops-event-guide]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
